/**
 * Debt Strategy — clearing a client's debts as part of the plan, funded from the same assets as the RAD.
 *
 * WHY IT SITS ABOVE RAD FUNDING. Money spent retiring a debt is money that cannot pay a deposit, so the
 * page reads in the order the money moves: decide the debts, then see what is left to fund the room.
 * The engine runs the two in that same order (`debtPayoffPlugin` at order 3, the RAD sale at 4), so the
 * screen and the projection agree about who claims a contested dollar first.
 *
 * WHY THE POOL IS SHARED. A dollar can pay a deposit or retire a debt, not both, and the whole advice
 * is in that competition. So both cards allocate against the same asset list and each slider's ceiling
 * is what the OTHER card has left of it (`reservedByAsset`). Nothing has to be reconciled afterwards
 * because nothing can be double-allocated in the first place.
 *
 * WHY IT IS HIDDEN WHEN THERE IS NO DEBT. It would otherwise be a permanent empty card on the busiest
 * screen in the product, and an invitation to wonder what was missed.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO is tell the adviser whether to clear the debt. It shows the
 * interest being paid and what the debt is secured against — the two facts that decide it — and leaves
 * the answer to the comparison, because the answer genuinely varies: an unsecured debt is dear and
 * counts against the assets test in full, a mortgage over an investment property already nets off that
 * property's assessed value, and one over an exempt family home does nothing for the means test at all.
 * The Suggestion Engine models both ways and ranks them; here the adviser models it themselves.
 */
import React, { useMemo, Dispatch, SetStateAction } from 'react';
import { CurrencyInput } from '../../components/ui/index.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { getPersonalLiquidAssetsForRad } from '../../utils/entityFundingUtils.ts';
import { calculateAnnualLoanRepayment } from '../../utils/structureLiabilitiesUtils.ts';
import { debtPayoffKey } from '../../../../shared/src/calculation/support/debtPayoff.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

type Allocation = Record<string, number>;

/** One of the client's debts, flattened out of the fact-find's category map with what the card needs. */
export interface PayableDebt {
    key: string;
    description: string;
    balance: number;
    interestRate: number;
    annualInterest: number;
    annualRepayment: number;
    /** What the debt is secured against, where that changes the means-test answer. */
    securedAgainst: 'home' | 'investmentProperty' | null;
}

/**
 * Every debt worth offering to pay out.
 *
 * Reverse mortgages are excluded: they are not serviced from cashflow and are settled from the estate,
 * so "pay it out early" is a different product decision this card cannot model. A debt with nothing
 * owing is excluded for the obvious reason.
 */
export function payableDebtsOf(factFind: DraftFactFind): PayableDebt[] {
    const debts: PayableDebt[] = [];
    for (const [category, list] of Object.entries(factFind.liabilities || {})) {
        for (const liability of list || []) {
            if (!liability?.id) continue;
            if (liability.type === 'reverse_mortgage') continue;
            const balance = Number(liability.balance) || 0;
            if (balance <= 0) continue;

            const interestRate = Number(liability.interestRate) || 0;
            const isMortgage = category === 'mortgages' || liability.type === 'mortgages';
            const assetLink = String(liability.assetLink || '');
            debts.push({
                key: debtPayoffKey(category, liability.id),
                description: String(liability.description || 'Debt'),
                balance,
                interestRate,
                annualInterest: balance * (interestRate / 100),
                annualRepayment: calculateAnnualLoanRepayment(liability),
                securedAgainst: !isMortgage || !assetLink || assetLink === 'none'
                    ? null
                    : assetLink === 'home' ? 'home' : 'investmentProperty',
            });
        }
    }
    return debts;
}

/**
 * The line under each debt: what holding it costs, and where that cost lands in the means test.
 *
 * Kept to facts rather than a recommendation. An adviser reading "$4,500 a year in interest, and the
 * cash held against it counts in full" has what they need to decide; being told the answer would be
 * wrong as often as it was right.
 */
function debtContextLine(debt: PayableDebt): string {
    const interest = debt.interestRate > 0
        ? `Holding it costs about ${formatCurrency(Math.round(debt.annualInterest))} a year in interest at ${debt.interestRate}%.`
        : 'No interest rate recorded, so no interest cost is modelled — add one on the Liabilities step to compare properly.';

    // The means-test half, which is what makes this a strategy rather than budgeting.
    const meansTest = debt.securedAgainst === 'investmentProperty'
        ? ' It is secured against an investment property, whose assessed value is already reduced by this debt — so clearing it moves the means test far less than an unsecured debt would.'
        : debt.securedAgainst === 'home'
            ? ' It is secured against the family home. While that home is exempt, the debt is doing nothing for the means test, so clearing it with assessable cash reduces assessable assets in full.'
            : ' It does not reduce assessable assets, but the cash held against it does — so clearing it reduces assessable assets in full.';

    return `${interest}${meansTest}`;
}

interface DebtPayoffStrategyProps {
    factFind: DraftFactFind;
    /** How much of each debt to clear, keyed by `debtPayoffKey`. */
    debtPayments: Allocation;
    setDebtPayments: Dispatch<SetStateAction<Allocation>>;
    /** Which assets fund it — the same `type-id` key space the RAD allocator uses. */
    debtFunding: Allocation;
    setDebtFunding: Dispatch<SetStateAction<Allocation>>;
    /** What a PART payment does to the repayment — only meaningful when a payoff is partial. */
    repaymentBasis?: 'keep_repayment' | 'lower_repayment';
    setRepaymentBasis?: (basis: 'keep_repayment' | 'lower_repayment') => void;
    /**
     * The sentence naming what ELSE these assets could fund in THIS module — the competition the card
     * exists to make visible. Defaults to the aged-care wording; every other module must pass its own,
     * or a Retirement Village page ends up telling a client their money "funds the accommodation
     * deposit" for a room that does not exist in their plan.
     */
    competingUseNote?: string;
    /** What the RAD allocator has already claimed of each asset, so this card cannot spend it twice. */
    reservedByAsset?: Allocation;
}

const DebtPayoffStrategy = ({
    factFind,
    debtPayments,
    setDebtPayments,
    debtFunding,
    setDebtFunding,
    repaymentBasis = 'keep_repayment',
    setRepaymentBasis,
    reservedByAsset = {},
    competingUseNote = 'The money comes from the same assets that fund the accommodation deposit, so what you spend here reduces what is left to fund the room.',
}: DebtPayoffStrategyProps) => {
    const debts = useMemo(() => payableDebtsOf(factFind), [factFind.liabilities]);
    const liquidAssets = useMemo(() => getPersonalLiquidAssetsForRad(factFind), [factFind.assets]);

    // The card does not exist for a client with no debt. Checked here rather than by the caller so the
    // rule lives with the card it governs.
    if (debts.length === 0) return null;

    const totalToRepay = debts.reduce((sum, debt) => sum + (debtPayments[debt.key] || 0), 0);
    const totalFunded = Object.values(debtFunding).reduce((sum, amount) => sum + (Number(amount) || 0), 0);
    // Gross, not net: share CGT is struck when the sale actually happens, so this is deliberately the
    // headline "raised" figure and the engine reports the shortfall if tax eats into it.
    const stillToFund = Math.max(0, totalToRepay - totalFunded);
    // Whether the repayment question can change anything at all — it only bites where a debt is part
    // paid and still being serviced afterwards.
    const hasPartialPayoff = debts.some((debt) => {
        const allocated = debtPayments[debt.key] || 0;
        return allocated > 0 && allocated < debt.balance;
    });
    const repaymentsFreed = debts
        .filter((debt) => (debtPayments[debt.key] || 0) >= debt.balance)
        .reduce((sum, debt) => sum + debt.annualRepayment, 0);

    /**
     * Spreads `amount` across the client's liquid assets, cash first, respecting what the deposit has
     * already claimed of each.
     *
     * Cash first because it is the obvious source and carries no CGT and no disposal decision — an
     * adviser paying out a loan reaches for the bank account, and having to build that by hand every
     * time was the friction. Everything it produces stays adjustable: this sets a starting point, it
     * does not lock anything.
     */
    const spreadAcrossAssets = (amount: number): Allocation => {
        const cashFirst = [...liquidAssets].sort(
            (a, b) => Number(b.typeId === 'bankAccounts') - Number(a.typeId === 'bankAccounts'),
        );
        const spread: Allocation = {};
        let left = amount;
        for (const asset of cashFirst) {
            if (left <= 0) break;
            const room = Math.max(0, (parseFloat(String(asset.value)) || 0) - (reservedByAsset[asset.assetId] || 0));
            const take = Math.min(left, room);
            if (take <= 0) continue;
            spread[asset.assetId] = take;
            left -= take;
        }
        return spread;
    };

    /**
     * Changing what is repaid re-funds it automatically.
     *
     * The card previously left funding at zero until it was allocated by hand, so the commonest action
     * — "pay this out" — produced a card that said the repayment would come from cash while every
     * asset slider sat untouched at its opening balance. The two halves disagreed on screen. Setting a
     * default is what makes the sliders mean what the totals say.
     */
    const changePayment = (key: string, balance: number, raw: string | number | undefined) => {
        const value = Math.min(Math.max(0, parseFloat(String(raw)) || 0), balance);
        const nextPayments = { ...debtPayments, [key]: value };
        setDebtPayments(nextPayments);
        const nextTotal = debts.reduce((sum, debt) => sum + (nextPayments[debt.key] || 0), 0);
        setDebtFunding(spreadAcrossAssets(nextTotal));
    };

    /**
     * A funding slider is capped at what is still NEEDED as well as at what the asset holds — so the
     * pool cannot be over-drawn for a $50,000 debt just because a $150,000 account exists. Moving one
     * slider down leaves the shortfall for another to pick up, which is the point of being able to
     * change them at all.
     */
    const changeFunding = (assetId: string, availableHere: number, raw: string | number | undefined) => {
        const asked = Math.max(0, parseFloat(String(raw)) || 0);
        const fundedElsewhere = Object.entries(debtFunding)
            .filter(([key]) => key !== assetId)
            .reduce((sum, [, amount]) => sum + (Number(amount) || 0), 0);
        const roomLeftToFund = Math.max(0, totalToRepay - fundedElsewhere);
        setDebtFunding((prev) => ({ ...prev, [assetId]: Math.min(asked, availableHere, roomLeftToFund) }));
    };

    return (
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-800 mb-1">Debt Strategy</h3>
            <p className="text-sm text-gray-600 mb-2">
                Clear some or all of {factFind.client?.firstName || 'the client'}&apos;s debts as part of this plan.{' '}
                {competingUseNote}
            </p>
            {/*
              * THE COUNTER-INTUITIVE PART, recovered from the retired insight panel.
              *
              * Clearing a debt moves cash out and a liability with it, so net worth barely shifts —
              * while the Age Pension and the means-tested fee can move a long way. Without saying
              * so, the screen looks like it has double-counted something, which is exactly how an
              * adviser reads a pension that moved when net worth did not.
              */}
            <p className="text-sm text-gray-600 mb-5">
                Clearing a debt does not change{' '}
                <strong>your net worth</strong> — the cash goes and so does what you owed. What it changes is{' '}
                <strong>the amount assessed for the Age Pension and aged care means tests</strong>, because the
                cash was counted and the debt mostly was not. That is why the figures beside this can move
                while your net worth stands still.
            </p>

            <div className="space-y-5">
                {debts.map((debt) => {
                    const allocated = debtPayments[debt.key] || 0;
                    const clearsInFull = allocated >= debt.balance;
                    return (
                        <div key={debt.key} className="border border-slate-200 rounded-lg p-4">
                            <div className="flex items-baseline justify-between gap-3 flex-wrap">
                                <span className="font-semibold text-gray-800">{debt.description}</span>
                                <span className="text-sm text-gray-600">
                                    {formatCurrency(debt.balance)} owing
                                    {debt.interestRate > 0 ? ` at ${debt.interestRate}%` : ''}
                                </span>
                            </div>
                            <div className="flex items-center gap-2 mt-3">
                                <input
                                    type="range"
                                    min="0"
                                    max={debt.balance}
                                    value={allocated}
                                    onChange={(e) => changePayment(debt.key, debt.balance, e.target.value)}
                                    aria-label={`Amount to repay off ${debt.description}`}
                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
                                />
                                <div className="w-36">
                                    <CurrencyInput
                                        value={allocated || 0}
                                        onChange={(v) => changePayment(debt.key, debt.balance, v)}
                                        placeholder="0"
                                        className=""
                                    />
                                </div>
                                {/* One click for the commonest choice, since "clear it entirely" is what an
                                    adviser reaches for far more often than a part payment. */}
                                <button
                                    type="button"
                                    onClick={() => changePayment(debt.key, debt.balance, clearsInFull ? 0 : debt.balance)}
                                    className={`px-3 py-2 text-sm font-semibold rounded-lg whitespace-nowrap transition-colors ${
                                        clearsInFull
                                            ? 'bg-teal-600 text-white hover:bg-teal-700'
                                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                    }`}
                                >
                                    {clearsInFull ? 'Paying out' : 'Pay out'}
                                </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">{debtContextLine(debt)}</p>
                            {allocated > 0 && !clearsInFull && (
                                <p className="text-xs text-gray-600 mt-1">
                                    Part payment: {formatCurrency(debt.balance - allocated)} stays owing and is still serviced.
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Shown only where it can change an outcome: a debt cleared outright has no repayment
                left to decide about, so asking would be noise on the commonest path. */}
            {hasPartialPayoff && setRepaymentBasis && (
                <label className="mt-5 flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer">
                    <input
                        type="checkbox"
                        checked={repaymentBasis === 'keep_repayment'}
                        onChange={(e) => setRepaymentBasis(e.target.checked ? 'keep_repayment' : 'lower_repayment')}
                        className="mt-0.5 h-4 w-4 accent-teal-600"
                    />
                    <span className="text-sm text-gray-700">
                        <span className="font-semibold">Keep the repayment the same and clear the debt sooner</span>
                        <span className="block text-xs text-gray-500 mt-0.5">
                            {repaymentBasis === 'keep_repayment'
                                ? 'What most lenders do unless asked otherwise. The balance is retired earlier and less interest is paid.'
                                : 'Unticked: the repayment is lowered over the remaining term instead. That frees cashflow now and costs more interest overall — which can be the better trade when care fees are tight.'}
                        </span>
                    </span>
                </label>
            )}

            {totalToRepay > 0 && (
                <div className="mt-6">
                    <h4 className="font-semibold text-gray-800 mb-1">Funded from</h4>
                    <p className="text-xs text-gray-500 mb-3">
                        Cash is used first by default. Lower a slider to draw on a different asset instead — each one
                        stops at whatever the accommodation deposit has left of it.
                    </p>
                    <div className="space-y-4">
                        {liquidAssets.map((asset) => {
                            const assetValue = parseFloat(String(asset.value)) || 0;
                            const reserved = reservedByAsset[asset.assetId] || 0;
                            const availableHere = Math.max(0, assetValue - reserved);
                            const allocated = debtFunding[asset.assetId] || 0;
                            // The slider stops at whatever is smaller: what this asset holds, or what
                            // is still needed. Selling $150,000 to repay a $50,000 debt is not a
                            // position anyone means to model, and the handler refuses it either way —
                            // this is what stops the TRACK offering it in the first place.
                            const fundedElsewhere = totalFunded - allocated;
                            const sliderMax = Math.min(availableHere, Math.max(0, totalToRepay - fundedElsewhere));
                            return (
                                <div key={asset.assetId}>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {asset.description || asset.typeName}
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="range"
                                            min="0"
                                            max={sliderMax}
                                            value={allocated}
                                            onChange={(e) => changeFunding(asset.assetId, availableHere, e.target.value)}
                                            aria-label={`Amount of ${asset.description || asset.typeName} to sell for debt repayment`}
                                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
                                        />
                                        <div className="w-36">
                                            <CurrencyInput
                                                value={allocated || 0}
                                                onChange={(v) => changeFunding(asset.assetId, availableHere, v)}
                                                placeholder="0"
                                                className=""
                                            />
                                        </div>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                        Available: {formatCurrency(availableHere)}
                                        {reserved > 0 && (
                                            <span className="text-gray-400">
                                                {' '}({formatCurrency(reserved)} allocated to the deposit)
                                            </span>
                                        )}
                                        {sliderMax < availableHere && (
                                            <span className="text-gray-400">
                                                {' '}· capped at the {formatCurrency(totalToRepay)} being repaid
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {totalToRepay > 0 && (
                <div className="mt-5 pt-4 border-t border-slate-200 space-y-1 text-sm">
                    <div className="flex justify-between">
                        <span>Total debt repaid:</span>
                        <span className="font-semibold">{formatCurrency(totalToRepay)}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Raised from assets:</span>
                        <span className="font-semibold">{formatCurrency(totalFunded)}</span>
                    </div>
                    {repaymentsFreed > 0 && (
                        <div className="flex justify-between text-teal-700">
                            <span>Repayments no longer paid:</span>
                            <span className="font-semibold">{formatCurrency(Math.round(repaymentsFreed))} a year</span>
                        </div>
                    )}
                    {/* Warned, not blocked. Under-funding is a real position — the shortfall comes out of
                        the year's cashflow — but it should never be a surprise, so it is stated plainly
                        rather than left to be inferred from a projection that looks tighter than expected. */}
                    {stillToFund > 0 && (
                        <p className="text-amber-700 text-xs pt-1">
                            {formatCurrency(stillToFund)} of this repayment is not funded by an asset sale. It will be
                            drawn from cash on hand, and any shortfall is met by the projection&apos;s year-end funding.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default DebtPayoffStrategy;
